export interface SocketClientOptions<TMessage, TCredentials> {
  url: () => string;
  protocols?: (credentials: TCredentials) => string[];
  reconnectDelayMs?: number;
  parse?: (raw: string) => TMessage;
  onOpen?: () => void;
  onParseError?: (error: unknown) => void;
}

type SocketMessageHandler<TMessage> = (message: TMessage) => void;

export class SocketClient<TMessage = unknown, TCredentials = string> {
  private socket: WebSocket | null = null;
  private handlers = new Set<SocketMessageHandler<TMessage>>();
  private reconnectTimer: number | null = null;
  private connectionVersion = 0;
  private shouldReconnect = false;
  private credentials: TCredentials | null = null;

  constructor(private readonly options: SocketClientOptions<TMessage, TCredentials>) {}

  connect(credentials: TCredentials): void {
    this.shouldReconnect = true;
    this.credentials = credentials;
    this.connectionVersion += 1;
    this.clearReconnectTimer();
    this.closeCurrentSocket();
    this.openSocket(this.connectionVersion);
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.credentials = null;
    this.connectionVersion += 1;
    this.clearReconnectTimer();
    this.closeCurrentSocket();
  }

  send(payload: unknown): boolean {
    if (!this.socket || this.socket.readyState !== 1) return false;
    this.socket.send(JSON.stringify(payload));
    return true;
  }

  subscribe(handler: SocketMessageHandler<TMessage>): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  private openSocket(version: number): void {
    if (!this.shouldReconnect || this.credentials === null) return;
    const socket = new WebSocket(
      this.options.url(),
      this.options.protocols?.(this.credentials)
    );
    this.socket = socket;

    socket.onopen = () => {
      if (this.socket !== socket) return;
      this.options.onOpen?.();
    };

    socket.onmessage = (event) => {
      if (this.socket !== socket) return;
      try {
        const parse = this.options.parse || ((raw: string) => JSON.parse(raw) as TMessage);
        const message = parse(String(event.data));
        this.handlers.forEach((handler) => handler(message));
      } catch (error) {
        this.options.onParseError?.(error);
      }
    };

    socket.onclose = () => {
      if (this.socket === socket) this.socket = null;
      if (!this.shouldReconnect || this.connectionVersion !== version) return;
      this.reconnectTimer = window.setTimeout(
        () => this.openSocket(version),
        this.options.reconnectDelayMs ?? 3000
      );
    };
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer === null) return;
    window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private closeCurrentSocket(): void {
    const socket = this.socket;
    this.socket = null;
    if (!socket) return;
    socket.onclose = null;
    socket.onopen = null;
    socket.onerror = null;
    socket.close();
  }
}
